import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PaginationBar from "../components/PaginationBar";
import { contactListAppPath } from "../lib/contactApiPaths";
import type { CSSProperties } from "react";

type AuditRow = {
  id: number;
  created_at: string;
  user_id: number | null;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  detail_json: string | null;
  ip: string | null;
  user_name: string | null;
  contact_entity_kind: string | null;
  group_name: string | null;
  intake_group_id: number | null;
};

const thTop: CSSProperties = { textAlign: "left", padding: "0.5rem", verticalAlign: "top" };
const tdTop: CSSProperties = { padding: "0.45rem 0.5rem", verticalAlign: "top" };

/** Korte Nederlandse uitleg bij technische actienamen (activiteitenlog). */
const AUDIT_ACTION_NL: Record<string, string> = {
  "beheer.merge_contacts": "Contactkaarten samengevoegd (dubbele opgeruimd)",
  "group.share_email": "Groepsmail verstuurd (deellijst / uitnodiging)",
  "group.member_detail_resolve": "Verzoek om aanvullende gegevens afgehandeld",
  "group.member_add_request_resolve": "Lidmaatschapsverzoek afgehandeld",
  "group.member.add": "Lid toegevoegd aan groep",
  "group.member.remove": "Lid verwijderd uit groep",
  "group.member_add_request": "Lidmaatschapsverzoek ingediend",
  "group.member_detail_request": "Verzoek om aanvullende gegevens ingediend",
  "group.member_taken_add": "Taak toegevoegd aan groepslid",
  "group.member_taken_remove": "Taak verwijderd bij groepslid",
  "group.admin_add": "Groepsbeheerder toegevoegd",
  "group.admin_remove": "Groepsbeheerder verwijderd",
  "group.viewer_add": "Groepskijker toegevoegd",
  "group.viewer_remove": "Groepskijker verwijderd",
  "group.chair_set": "Voorzitter ingesteld",
  "group.chair_remove": "Voorzitter verwijderd",
  "group.chair_demote_group_admin": "Groepsbeheer opnieuw ingericht bij voorzitterswijziging",
  "group.create": "Groep aangemaakt",
  "group.update": "Groep bijgewerkt",
  "group.delete": "Groep verwijderd",
  "taken.create": "Taak aangemaakt",
  "taken.update": "Taak bijgewerkt",
  "taken.delete": "Taak verwijderd",
  "admin.vrijwilliger_demote_to_dominicusganger": "Vrijwilliger teruggezet naar dominicusgangers (geen groepsrol)",
  "werkgroep_intake.approved": "Werkgroep-ledenlijst goedgekeurd en in het adresboek verwerkt",
  "werkgroep_intake.marked_no_op": "Werkgroep-intake afgehandeld zonder adresboekwijzigingen",
  "werkgroep_intake.rejected": "Werkgroep-ledenlijst afgekeurd",
  "werkgroep_intake.submitted": "Werkgroep-ledenlijst ingediend door voorzitter",
  "werkgroep_intake.invite_sent": "Uitnodiging werkgroep-ledenlijst verstuurd",
  "werkgroep_intake.link_generated": "Werkgroep-intakelink aangemaakt",
  "werkgroep_intake.revision_requested": "Herziening werkgroep-ledenlijst gevraagd",
};

function formatJsonPreview(raw: string | null, max: number) {
  if (raw == null || raw === "") return "—";
  try {
    const s = JSON.stringify(JSON.parse(raw));
    if (s.length <= max) return s;
    return s.slice(0, max) + "…";
  } catch {
    if (raw.length <= max) return raw;
    return raw.slice(0, max) + "…";
  }
}

/** Directe links naar groep, contactkaart of werkgroep-intake (indien van toepassing). */
function entityLinks(r: AuditRow): { to: string; label: string }[] {
  const out: { to: string; label: string }[] = [];
  if (r.entity_type === "group" && r.entity_id != null) {
    out.push({
      to: `/groups/${r.entity_id}`,
      label: r.group_name ? `Groep: ${r.group_name}` : "Groep",
    });
  }
  if (r.entity_type === "contact" && r.entity_id != null) {
    const base = contactListAppPath(r.contact_entity_kind);
    out.push({ to: `${base}?contactId=${r.entity_id}`, label: "Contactkaart" });
  }
  if (r.entity_type === "werkgroep_intake" && r.entity_id != null) {
    out.push({ to: `/aanvragen?tab=intakes&intake=${r.entity_id}`, label: "Ledenlijst-intake" });
    if (r.intake_group_id != null) {
      out.push({
        to: `/groups/${r.intake_group_id}`,
        label: r.group_name ? `Groep: ${r.group_name}` : "Groepspagina",
      });
    }
  }
  return out;
}

/**
 * Lijst met geregistreerde acties (import, wijzigen, in- en uitloggen, …).
 * Alleen zichtbaar voor beheer-accounts; route wordt in App afgeschermd.
 */
export default function AdminAuditLogPage() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [q, setQ] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setPage(1);
  }, [q, actionFilter, entityTypeFilter, perPage]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (q.trim()) params.q = q.trim();
      if (actionFilter.trim()) params.action = actionFilter.trim();
      if (entityTypeFilter.trim()) params.entity_type = entityTypeFilter.trim();
      const res = await api.get<{ data: AuditRow[]; total: number; page: number; per_page: number }>("/api/audit", {
        params,
      });
      setRows(res.data.data);
      setTotal(res.data.total ?? 0);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      setLoadError(ax.response?.data?.error ?? ax.message ?? "Laden mislukt");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, q, actionFilter, entityTypeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to="/medewerkers" className="btn-back" style={{ display: "inline-block", marginBottom: "0.5rem" }}>
            ← App
          </Link>
          <h2 className="page-title">Beheer: activiteiten</h2>
          <p className="page-subtitle">
            Wie wat wanneer in het systeem heeft gedaan (o.a. wijzigen, importeren, aan- en afmelden).{" "}
            {loading ? "…" : `${total} ${total === 1 ? "regel" : "regels"}`}
            {" "}
            · <strong>Geschiedenis per contactkaart</strong> (reis van die kaart): open de kaart in de lijst en scroll in
            het venster naar <em>Geschiedenis van deze kaart</em> (alleen voor wie het activiteitenlog mag zien).
          </p>
        </div>
      </div>

      <div className="filters">
        <div className="filters-start" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
          <div className="search-box" style={{ minWidth: 200, flex: "1 1 200px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Zoeken (actie, type, detail, naam, …)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Vrije zoekterm"
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140 }}>
            <label className="data-list-row-muted" style={{ fontSize: "0.75rem" }}>
              Actie (exact)
            </label>
            <input
              type="text"
              className="search-input"
              style={{ padding: "8px 10px" }}
              placeholder="bijv. contact.create"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 140 }}>
            <label className="data-list-row-muted" style={{ fontSize: "0.75rem" }}>
              Entiteitstype
            </label>
            <input
              type="text"
              className="search-input"
              style={{ padding: "8px 10px" }}
              placeholder="bijv. contact, group"
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loadError && <p className="form-error" style={{ marginBottom: "1rem" }}>{loadError}</p>}

      {loading && rows.length === 0 ? (
        <div className="loading-list">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton-card" style={{ height: 48 }} />
          ))}
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thTop}>Tijd</th>
                  <th style={thTop}>Gebruiker</th>
                  <th style={thTop}>Actie</th>
                  <th style={thTop}>Type</th>
                  <th style={thTop}>ID</th>
                  <th style={thTop}>Links</th>
                  <th style={thTop}>IP</th>
                  <th style={thTop}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="data-list-row-muted" style={{ padding: "1.5rem" }}>
                      {loadError ? "—" : "Geen regels. Pas filters of zoekterm aan."}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const links = entityLinks(r);
                    const actionNl = AUDIT_ACTION_NL[r.action] ?? null;
                    return (
                      <tr key={r.id} style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                        <td style={{ ...tdTop, whiteSpace: "nowrap" }}>
                          {r.created_at.replace("T", " ").replace(/\.\d+Z?$/, "")}
                        </td>
                        <td style={tdTop}>
                          {r.user_name ?? (r.user_id == null ? "—" : `user #${r.user_id}`)}
                        </td>
                        <td style={tdTop}>
                          <code style={{ fontSize: "0.85em" }}>{r.action}</code>
                          {actionNl ? (
                            <div className="data-list-row-muted" style={{ fontSize: "0.78rem", marginTop: 3 }}>
                              {actionNl}
                            </div>
                          ) : null}
                        </td>
                        <td style={tdTop}>{r.entity_type ?? "—"}</td>
                        <td style={tdTop}>{r.entity_id ?? "—"}</td>
                        <td style={{ ...tdTop, fontSize: "0.85rem" }}>
                          {links.length === 0 ? (
                            <span className="data-list-row-muted">—</span>
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                              {links.map((l) => (
                                <li key={l.to} style={{ marginBottom: 2 }}>
                                  <Link to={l.to}>{l.label}</Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td style={{ ...tdTop, fontSize: "0.85em" }}>{r.ip ?? "—"}</td>
                        <td style={{ ...tdTop, maxWidth: 220, fontSize: "0.8rem", wordBreak: "break-all" }}>
                          {formatJsonPreview(r.detail_json, 120)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <PaginationBar
              page={page}
              perPage={perPage}
              total={total}
              itemLabel="regels"
              onPageChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
